import React from "react";
import Navbar from "../Navbar/Navbar";
import SearchTabs from "./SearchTabs/SearchTabs";
import TravelApp from "./TravelApp/TravelApp";
import Footer from "../Footer/Footer";
import PromoCarousel from "./PromoCarousel/PromoCarousel";
import DealsForYou from "./DealsForYou/DealsForYou";
import Recommendations from "./Recommendations/Recommendations";
import HomeGuides from "./Guides/HomeGuides";
import SEO, { SITE_URL } from "../SEO/SEO";
import "./home.scss";

const SEO_DESTINATIONS = [
  ["Japan eSIM", "/esim/japan"],
  ["Singapore eSIM", "/esim/singapore"],
  ["South Korea eSIM", "/esim/south-korea"],
  ["Thailand eSIM", "/esim/thailand"],
  ["Malaysia eSIM", "/esim/malaysia"],
  ["Vietnam eSIM", "/esim/vietnam"],
  ["Taiwan eSIM", "/esim/taiwan"],
  ["Hong Kong eSIM", "/esim/hong-kong"],
  ["Australia eSIM", "/esim/australia"],
  ["USA eSIM", "/esim/united-states"],
];

const HOME_FAQS = [
  {
    question: "What can I use Teleport Now for?",
    answer:
      "Teleport Now provides travel eSIM search, flight search, Things To Do discovery, airport lounge services, booking information, and trip support through its website and mobile app.",
  },
  {
    question: "Where are current prices and availability shown?",
    answer:
      "Dynamic prices and availability are shown in Teleport's live service searches. Public information pages do not publish fixed prices.",
  },
  {
    question: "Can I use Teleport on the web and in the app?",
    answer:
      "Yes. Public service and guide pages work on the web, while the mobile app provides the full supported travel experience.",
  },
];

export default function Home() {
  const featureCards = [
    {
      title: "Buy travel eSIMs in minutes",
      body: "Pick local, regional, or global coverage and keep data ready before your trip starts.",
    },
    {
      title: "Keep bookings in one place",
      body: "Manage trips, purchases, support, and receipts from a single travel account.",
    },
    {
      title: "Unlock app-only travel deals",
      body: "Use promotions, referral rewards, and partner offers built for frequent travelers.",
    },
  ];

  return (
    <>
      <SEO
        title="Teleport Now | eSIMs, Flights & Travel Services"
        description="Teleport Now provides travel eSIM search, flight search, Things To Do discovery, airport lounge access and trip support through its website and mobile app."
        canonicalPath="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: "Teleport Now",
              url: SITE_URL,
              logo: `${SITE_URL}/logo512.png`,
            },
            {
              "@type": "WebSite",
              "@id": `${SITE_URL}/#website`,
              name: "Teleport Now",
              url: SITE_URL,
            },
            {
              "@type": "SoftwareApplication",
              "@id": `${SITE_URL}/#app`,
              name: "Teleport Now",
              applicationCategory: "TravelApplication",
              operatingSystem: "iOS, Android",
              description:
                "Travel application for eSIM search, flights, Things To Do, airport lounge services and trip support.",
              url: SITE_URL,
              downloadUrl: `${SITE_URL}/download`,
              publisher: { "@id": `${SITE_URL}/#organization` },
            },
            {
              "@type": "ItemList",
              "@id": `${SITE_URL}/#features`,
              name: "Teleport Now travel app features",
              itemListElement: featureCards.map((feature, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: feature.title,
                description: feature.body,
              })),
            },
            {
              "@type": "ItemList",
              "@id": `${SITE_URL}/#popular-esim-destinations`,
              name: "Popular travel eSIM destinations",
              itemListElement: SEO_DESTINATIONS.map(([name, href], index) => ({
                "@type": "ListItem",
                position: index + 1,
                name,
                url: `${SITE_URL}${href}`,
              })),
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}/#faq`,
              mainEntity: HOME_FAQS.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: { "@type": "Answer", text: faq.answer },
              })),
            },
          ],
        }}
      />

      <Navbar />

      <main className="editorial-home teleport-page-shell">
        <section className="editorial-hero" aria-labelledby="home-hero-title">
          <picture className="editorial-hero__media">
            <source srcSet="/images/editorial/mountain-hero.webp" type="image/webp" />
            <img src="/images/editorial/mountain-hero.png" alt="" fetchPriority="high" />
          </picture>
          <div className="editorial-hero__veil" aria-hidden="true" />
          <div className="container editorial-hero__content">
            <h1 id="home-hero-title">
              <span>Your next adventure starts here.</span>
              <span>Plan smarter, stay connected and save more with Teleport Now.</span>
            </h1>
            <p>
              Book your travel essentials, stay connected with instant eSIM activation,
              manage bookings effortlessly, and unlock exclusive in-app deals. Less stress,
              more adventures — all with Teleport Now
            </p>
          </div>
        </section>

        <section className="editorial-search" id="travel-search" aria-label="Travel services">
          <div className="container">
            <SearchTabs />
          </div>
        </section>

        <div className="container editorial-content">
          <DealsForYou />
          <PromoCarousel />
          <Recommendations />
          <HomeGuides />
        </div>

        <section className="travel-app-section">
          <div className="container"><TravelApp /></div>
        </section>
      </main>

      <Footer />
    </>
  );
}
